import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  FormGroup,
  Row,
  Spinner,
} from "react-bootstrap";
import { toast } from "react-toastify";
import api from "common/api";
import * as formik from "formik";
import * as yup from "yup";
import { useAuth } from "authContext";
import ReservationDetailsCard from "./ReservationDetailsCard";
import StudentDetailsCard from "./StudentDetailsCard";

interface OrderPaymentsLessonProps {
  reservation: any;
  selectedSubject: any;
  notes: string;
  paymentInfo: any;
  onBookingComplete: (id: number) => void;
  onBookingError: () => void;
}

const OrderPaymentsLesson: React.FC<OrderPaymentsLessonProps> = ({
  reservation,
  selectedSubject,
  notes: initialNotes,
  paymentInfo,
  onBookingComplete,
  onBookingError,
}) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [countries, setCountries] = useState<{ id: number; name: string }[]>(
    [],
  );
  const { userProfile } = useAuth();
  const [notes, setNotes] = useState<string>(initialNotes || "");
  const [selectedGrade, setSelectedGrade] = useState<string | null>(null);
  const [schoolType, setSchoolType] = useState<"osnovna" | "srednja">(
    "osnovna",
  );
  const [notesTouched, setNotesTouched] = useState(false);
  const [initialValues, setInitialValues] = useState({
    fullName: "",
    address: "",
    city: "",
    zipcode: "",
    country: "",
    phone: "",
    email: "",
    termsAccepted: false,
  });
  const { Formik } = formik;

  const schema = yup.object({
    fullName: yup
      .string()
      .required("Potrebno je unijeti ime platitelja")
      .min(3, "Ime mora imati najmanje 3 znaka"),
    address: yup
      .string()
      .required("Potrebno je unijeti adresu platitelja")
      .min(3, "Adresa mora imati najmanje 3 znaka"),
    city: yup
      .string()
      .required("Potrebno je unijeti grad platitelja")
      .min(3, "Grad mora imati najmanje 3 znaka"),
    zipcode: yup
      .string()
      .required("Potrebno je unijeti poštanski broj platitelja")
      .min(3, "Poštanski broj mora imati najmanje 3 znaka"),
    country: yup.string().required("Potrebno je unijeti državu platitelja"),
    phone: yup
      .string()
      .required("Potrebno je unijeti kontakt broj")
      .min(3, "Kontakt broj mora imati najmanje 3 znaka"),
    email: yup
      .string()
      .email("Unesite ispravnu email adresu")
      .required("Potrebno je unijeti email adresu"),
    termsAccepted: yup
      .boolean()
      .oneOf([true], "Morate prihvatiti uvjete kupnje"),
  });

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await api.get(`/countries/`);
        if (Array.isArray(response.data)) {
          setCountries(response.data);
          const hrvatska = response.data.find(
            (country) => country.name === "Hrvatska",
          );
          if (hrvatska) {
            setInitialValues((prev) => ({
              ...prev,
              country: String(hrvatska.id),
              email: userProfile?.email || "",
            }));
          }
        } else {
          console.error("Unexpected response format:", response.data);
          toast.error("Neispravan format podataka za države");
        }
      } catch (error) {
        console.error(error);
        toast.error("Greška prilikom učitavanja država");
      }
    };

    fetchCountries();

    if (userProfile?.email) {
      setInitialValues((prev) => ({
        ...prev,
        email: userProfile.email,
      }));
    }
  }, [userProfile]);

  const handleSubmit = async (formData: any, { setSubmitting }: any) => {
    if (!reservation.calendarId || !reservation.reservationToken) {
      toast.error("Podaci o rezervaciji nisu dostupni");
      onBookingError();
      return;
    }

    if (!selectedGrade || !notes.trim()) {
      toast.error("Potrebno je odabrati razred i unijeti napomenu");
      return;
    }

    try {
      setLoading(true);

      let finalNotes = notes;
      if (selectedGrade) {
        const gradeInfo = `${
          schoolType === "osnovna" ? "Osnovna škola" : "Srednja škola"
        }: ${selectedGrade}. razred`;
        finalNotes = finalNotes ? `${gradeInfo} - ${finalNotes}` : gradeInfo;
      }

      const bookingResponse = await api.post(
        `/calendars/${reservation.calendarId}/book/`,
        {
          reservation_token: reservation.reservationToken,
          remark: finalNotes,
          subject: selectedSubject?.id,
        },
      );

      if (bookingResponse.data && bookingResponse.data.id) {
        const bookingId = bookingResponse.data.id;
        onBookingComplete(bookingId);

        const paymentRequestData = {
          order_id: bookingId,
          full_name: formData.fullName,
          address: formData.address,
          city: formData.city,
          zipcode: formData.zipcode,
          country: formData.country,
          phone: formData.phone,
          email: formData.email,
        };

        const paymentResponse = await api.post(
          `/orders/${bookingId}/pay/`,
          paymentRequestData,
        );

        const { payment_form_url } = paymentResponse.data;
        window.location.href = payment_form_url;
        navigate(
          `/placanje-u-tijeku?paymentFormUrl=${encodeURIComponent(
            payment_form_url,
          )}`,
        );
      } else {
        throw new Error("Dobiven je neispravan odgovor prilikom rezervacije");
      }
    } catch (error) {
      console.error("Error in payment process:", error);
      toast.error("Greška prilikom plaćanja");
      onBookingError();
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  const isFormValid = selectedGrade !== null && notes.trim().length > 0;

  return (
    <div>
      <Row className="mt-4">
        <Col>
          <h4 className="mb-4">Plaćanje poduke</h4>
          {paymentInfo && <ReservationDetailsCard paymentInfo={paymentInfo} />}

          <StudentDetailsCard
            schoolType={schoolType}
            setSchoolType={setSchoolType}
            selectedGrade={selectedGrade}
            setSelectedGrade={setSelectedGrade}
            notes={notes}
            setNotes={setNotes}
            notesTouched={notesTouched}
            setNotesTouched={setNotesTouched}
          />

          <Formik
            validationSchema={schema}
            onSubmit={handleSubmit}
            enableReinitialize={true}
            initialValues={initialValues}
          >
            {({
              handleSubmit,
              handleChange,
              isSubmitting,
              handleBlur,
              values,
              touched,
              errors,
            }) => (
              <Form noValidate onSubmit={handleSubmit}>
                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formFullName"
                  >
                    <Form.Label>Ime i prezime platitelja</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite puno ime"
                      name="fullName"
                      value={values.fullName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.fullName && !!errors.fullName}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.fullName}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formAddress"
                  >
                    <Form.Label>Adresa</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite adresu"
                      name="address"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.address && !!errors.address}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.address}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formCity"
                  >
                    <Form.Label>Grad</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite grad"
                      name="city"
                      value={values.city}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.city && !!errors.city}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.city}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formZipcode"
                  >
                    <Form.Label>Poštanski broj</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite poštanski broj"
                      name="zipcode"
                      value={values.zipcode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.zipcode && !!errors.zipcode}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.zipcode}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formCountry"
                  >
                    <Form.Label>Država</Form.Label>
                    <Form.Control
                      as="select"
                      name="country"
                      value={values.country}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.country && !!errors.country}
                    >
                      <option value="">Odaberite državu</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </Form.Control>
                    <Form.Control.Feedback type="invalid">
                      {errors.country}
                    </Form.Control.Feedback>
                  </FormGroup>

                  <FormGroup
                    as={Col}
                    lg={6}
                    className="mb-3"
                    controlId="formPhone"
                  >
                    <Form.Label>Kontakt broj</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Unesite kontakt broj"
                      name="phone"
                      value={values.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      isInvalid={touched.phone && !!errors.phone}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.phone}
                    </Form.Control.Feedback>
                  </FormGroup>
                </Row>

                <Row>
                  <Col lg={6}>
                    <FormGroup className="mb-3" controlId="formEmail">
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="email"
                        placeholder="Unesite email adresu"
                        name="email"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={touched.email && !!errors.email}
                      />
                      <Form.Control.Feedback type="invalid">
                        {errors.email}
                      </Form.Control.Feedback>
                    </FormGroup>

                    <FormGroup className="mb-3" controlId="formTerms">
                      <Form.Check
                        type="checkbox"
                        label={
                          <>
                            Slažem se s uvjetima kupnje opisane u{" "}
                            <a
                              href="/opci-uvjeti"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Općim uvjetima
                            </a>
                          </>
                        }
                        name="termsAccepted"
                        checked={values.termsAccepted}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        isInvalid={
                          touched.termsAccepted && !!errors.termsAccepted
                        }
                        feedback={errors.termsAccepted}
                      />
                    </FormGroup>
                  </Col>

                  <Col lg={6}>
                    <Card className="mb-3 shadow-sm">
                      <Card.Header className="bg-light">
                        <h6 className="mb-0">Sažetak narudžbe</h6>
                      </Card.Header>
                      <Card.Body>
                        {paymentInfo && (
                          <>
                            <p>
                              PDV (25%): {(+paymentInfo.price * 0.2).toFixed(2)}{" "}
                              EUR
                            </p>
                            <h5 className="text-end mb-0">
                              Ukupni iznos:{" "}
                              <span className="text-primary">
                                {paymentInfo.price} EUR
                              </span>
                            </h5>
                          </>
                        )}
                      </Card.Body>
                    </Card>

                    <Button
                      variant="primary"
                      type="submit"
                      className="float-end"
                      disabled={
                        isSubmitting ||
                        !values.termsAccepted ||
                        loading ||
                        !isFormValid
                      }
                      onClick={() => setNotesTouched(true)}
                    >
                      {(isSubmitting || loading) && (
                        <span style={{ paddingRight: "10px" }}>
                          <Spinner size="sm" />
                        </span>
                      )}
                      <span>Nastavi na plaćanje</span>
                    </Button>
                  </Col>
                </Row>
              </Form>
            )}
          </Formik>
        </Col>
      </Row>
    </div>
  );
};

export default OrderPaymentsLesson;
